import React, { useEffect, useRef } from 'react';
import { Modal, Button, Container } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import '../styles/EulaModal.css';
import PropTypes from 'prop-types';

const EulaContent = ({ onAccept, onDecline, isModal = false }) => {
    const navigate = useNavigate();

    const handleAccept = () => {
        if (onAccept) onAccept();
        else navigate('/');
    };

    const handleDecline = () => {
        if (onDecline) onDecline();
        else navigate('/');
    };

    const acceptButtonRef = useRef(null);

    useEffect(() => {
        if(acceptButtonRef.current) {
            acceptButtonRef.current.focus();
        }
    }, []);

    const content = (
        <div 
            className={isModal ? 'eula-content customscrollbar' : 'py-5'}
            data-testid="eula-scroll-container"
        >
            <p>
                <strong>SBMS (Space Battle Management System)</strong><br/>
                Version 4.1 - 2026-06-17
            </p>

            <h2>IMPORTANT - READ CAREFULLY:</h2>
            <p>This End User License Agreement (&quot;Agreement&quot;) is a legal agreement between you (either an individual or a single entity) and Saber Astronautics LLC, the developers of the SBMS (Space Battle Management System) software (&quot;Licensor&quot;). By installing, copying, or using the SBMS software, you agree to be bound by the terms of this Agreement.</p>
            <p>If you do not agree to the terms of this Agreement, do not use the software.</p>

            <h2>1. License Grant</h2>
            <p>Subject to the terms and conditions of this Agreement, the Licensor hereby grants you a <strong>non-exclusive, non-transferable, revocable license</strong> to use the SBMS software solely for official U.S. Department of Defense (DoD) or U.S. Government purposes, or as explicitly authorized by the Licensor in writing for allied or partner nation use during approved exercises or operations.</p>

            <h2>2. Restrictions</h2>
            <p>You shall not:</p>
            <ul>
                <li>Modify, adapt, translate, reverse engineer, decompile, disassemble, or create derivative works based on the SBMS software, or otherwise attempt to discover its source code or underlying structure, ideas, or algorithms, except as and only to the extent that the foregoing restriction is prohibited by applicable law or permitted by the licensing terms governing any open-source components included with the software.</li>
                <li>Rent, lease, sublicense, sell, transfer, assign, or otherwise distribute the software to any third party not explicitly authorized under this Agreement.</li>
                <li>Use the software in a manner that violates U.S. laws or regulations, including but not limited to those governing defense technology and export controls.</li>
                <li>Disclose, reproduce, or distribute the software or any part thereof to unauthorized users or entities.</li>
                <li>Develop, publish, distribute, sell, or otherwise provide any training materials, courses, instruction, or related educational programs (whether for commercial gain or not) based on, referring to, or utilizing the SBMS software - in whole or in part - to any third party without the prior express written consent of Saber Astronautics.</li>
            </ul>

            <h2>3. Ownership</h2>
            <p>All title, ownership rights, and intellectual property rights in and to the SBMS software and any copies thereof remain the exclusive property of the Licensor. This Agreement does not convey to you any rights of ownership in or related to the SBMS software.</p>

            <h2>4. U.S. Government Rights</h2>
            <p>The SBMS software is &quot;commercial computer software&quot; and the accompanying documentation is &quot;commercial computer software documentation&quot; as those terms are used in 48 C.F.R. § 2.101, and is provided to the U.S. Government consistent with 48 C.F.R. § 12.212 (FAR) and 48 C.F.R. §§ 227.7202-1 through 227.7202-4 (DFARS). Consistent with these provisions, the software and documentation are licensed to U.S. Government end users (a) only as commercial items and (b) with only those rights granted to all other end users under this Agreement. To the extent any term of this Agreement conflicts with applicable U.S. federal law, the Federal Acquisition Regulation (FAR), or the Defense Federal Acquisition Regulation Supplement (DFARS), such law or regulation shall control to the extent of the conflict, and the remaining terms shall remain in full force and effect.</p>

            <h2>5. Export Control and Foreign Disclosure</h2>
            <p>You acknowledge that the SBMS software may be subject to U.S. export control laws and regulations, including the International Traffic in Arms Regulations (ITAR) and the Export Administration Regulations (EAR). You agree not to export, re-export, or otherwise transfer the software in violation of any such applicable laws or regulations.</p>
            <p>Use by foreign allied governments is strictly limited to cases authorized in writing by the Licensor and/or relevant U.S. Government authorities.</p>

            <h2>6. Use Limitations</h2>
            <p>The software is provided <strong>only</strong> for use in support of:</p>
            <ul>
                <li>U.S. DoD and Government operations, training, or mission planning;</li>
                <li>Allied or partner nation exercises or test environments with prior written approval;</li>
                <li>No use in commercial, non-governmental, or personal contexts is authorized unless authorized by Saber Astronautics in writing.</li>
            </ul>

            <h2>7. Data Collection and Use</h2>
            <p>You agree that the Licensor may collect and use technical data and related information gathered periodically to facilitate the provision of software updates, product support, and other services to you (if any) related to the software, provided that such information is maintained in a form that does not personally identify you and is handled consistent with applicable U.S. Government data-handling, privacy, and security requirements. The software may access - but does not store - confidential information you input in connection with its use.</p>

            <h2>8. Termination</h2>
            <p>This Agreement is effective until terminated. It will terminate automatically without notice from the Licensor if you fail to comply with any provision of this Agreement. Upon termination, you must cease all use of the software and destroy or, where required by applicable records-retention obligations, return all copies. Sections 3, 4, 5, 9, 10, 11, 12, and 13 survive any termination or expiration of this Agreement.</p>

            <h2>9. Disclaimer of Warranties</h2>
            <p>THE SOFTWARE IS PROVIDED <strong>&quot;AS IS&quot;</strong> AND <strong>&quot;AS AVAILABLE,&quot;</strong> WITH ALL FAULTS AND WITHOUT WARRANTY OF ANY KIND, EITHER EXPRESS, IMPLIED, OR STATUTORY. TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, THE LICENSOR HEREBY DISCLAIMS ALL WARRANTIES AND CONDITIONS WITH RESPECT TO THE SOFTWARE, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OR CONDITIONS OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, ACCURACY, QUIET ENJOYMENT, AND NON-INFRINGEMENT OF THIRD-PARTY RIGHTS. NO ORAL OR WRITTEN INFORMATION OR ADVICE GIVEN BY THE LICENSOR OR ITS AUTHORIZED REPRESENTATIVE SHALL CREATE A WARRANTY.</p>

            <h2>10. Limitation of Liability</h2>
            <p>TO THE EXTENT NOT PROHIBITED BY LAW, IN NO EVENT SHALL THE LICENSOR BE LIABLE FOR ANY INCIDENTAL, SPECIAL, INDIRECT, OR CONSEQUENTIAL DAMAGES WHATSOEVER, INCLUDING, WITHOUT LIMITATION, DAMAGES FOR LOSS OF PROFITS, LOSS OF DATA, BUSINESS INTERRUPTION, OR ANY OTHER COMMERCIAL DAMAGES OR LOSSES, ARISING OUT OF OR RELATED TO YOUR USE OF OR INABILITY TO USE THE SOFTWARE, HOWEVER CAUSED AND REGARDLESS OF THE THEORY OF LIABILITY (CONTRACT, TORT, OR OTHERWISE), EVEN IF THE LICENSOR HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OR LIMITATION OF LIABILITY FOR CERTAIN DAMAGES, SO THE ABOVE EXCLUSIONS AND LIMITATIONS MAY NOT APPLY TO YOU.</p>

            <h2>11. Survival</h2>
            <p>The provisions of this Agreement that by their nature should survive termination - including, without limitation, Ownership, U.S. Government Rights, Export Control and Foreign Disclosure, Disclaimer of Warranties, Limitation of Liability, and Governing Law and Jurisdiction - shall survive any termination or expiration of this Agreement.</p>

            <h2>12. Governing Law and Jurisdiction</h2>
            <p>Except to the extent governed by U.S. federal law (including where the licensee is the U.S. Government, in which case federal law and the FAR/DFARS apply), this Agreement and the relationship between you and the Licensor shall be governed by and interpreted in accordance with the laws of the State of Colorado, without regard to its conflict-of-laws principles. You and the Licensor consent to the exclusive jurisdiction of, and waive all defenses of lack of personal jurisdiction and forum non conveniens with respect to venue in, the state and federal courts located in the State of Colorado. Application of the United Nations Convention on Contracts for the International Sale of Goods and the Uniform Computer Information Transactions Act (UCITA) is excluded from this Agreement.</p>

            <h2>13. General</h2>
            <p>If any provision of this Agreement is held to be unenforceable or invalid, that provision shall be modified to the minimum extent necessary to make it enforceable, and the remaining provisions shall remain in full force and effect. This Agreement constitutes the entire agreement between you and the Licensor regarding the software and supersedes all prior or contemporaneous understandings regarding such subject matter. You may not assign or transfer this Agreement or any rights or obligations hereunder without the Licensor&apos;s prior written consent, and any attempted assignment in violation of this section is void; the Licensor may assign this Agreement without restriction. The Licensor&apos;s failure to enforce any right or provision of this Agreement shall not constitute a waiver of that right or provision. The Licensor may amend this Agreement from time to time; material changes will be presented for your acceptance, and your continued use of the software after the effective date of such changes constitutes acceptance of them.</p>

            <h2>14. Acknowledgment</h2>
            <p>By installing or using the SBMS software, you acknowledge that you have read this Agreement, understand it, and agree to be bound by its terms and conditions.</p>
            <br/>
        </div>
    );

    const buttons = (
        <div className="d-flex justify-content-end gap-2 mt-4">
            <Button 
                variant="secondary" 
                onClick={handleDecline}
                style={{ 
                    backgroundColor: '#333',
                    borderColor: '#333'
                }}
            >
                Decline
            </Button>
            <Button 
                variant="warning"
                onClick={handleAccept}
                style={{ 
                    backgroundColor: '#f5bc59',
                    borderColor: '#f5bc59'
                }}
                ref={acceptButtonRef}
                autoFocus
            >
                Accept
            </Button>
        </div>
    );

    if (isModal) {
        return (
            <>
                <Modal.Header className="bg-dark text-white border-0">
                    <Modal.Title>End User License Agreement (EULA)</Modal.Title>
                </Modal.Header>
                <Modal.Body className="bg-dark text-white p-0">
                    {content}
                </Modal.Body>
                <Modal.Footer className="bg-dark border-0">
                    {buttons}
                </Modal.Footer>
            </>
        );
    }

    return (
        <Container className="bg-dark text-white min-vh-100">
            <h1 className="text-warning mb-4">End User License Agreement (EULA)</h1>
            {content}
            {buttons}
        </Container>
    );
};

EulaContent.propTypes = {
    onAccept: PropTypes.func,
    onDecline: PropTypes.func,
    isModal: PropTypes.bool
};

const EulaModal = ({ show, onAccept, onDecline }) => {
    if (!show) return null;

    return (
        <Modal
            show={show}
            onHide={onDecline}
            centered
            size="lg"
            backdrop="static"
            keyboard={false}
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
            dialogClassName="eula-modal"
        >
            <EulaContent onAccept={onAccept} onDecline={onDecline} isModal={true} />
        </Modal>
    );
};

EulaModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onAccept: PropTypes.func.isRequired,
    onDecline: PropTypes.func.isRequired
};

export const EulaPage = () => {
    return <EulaContent />;
};

export default EulaModal; 